import React from 'react'
import PropTypes from 'prop-types'
import useWindowSize from 'react-use/lib/useWindowSize'
import Confetti from 'react-confetti'
import { useParams, Redirect, useHistory } from 'react-router-dom'
import { CheckCircle2, Printer, ArrowRight } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import useAuth from '../../../utilities/hooks/useAuth'
import useFacility from '../../../utilities/hooks/useFacility'
import { priceInDollars } from '../../../utilities/helpers'

function PaymentConfirmation({ lineItems, total, couponCodeAmount, couponCodePromotionAmount }) {
  const history = useHistory()
  const { width, height } = useWindowSize()
  const { isSignedIn, currentUser } = useAuth()
  const { facility } = useFacility()
  let { facility_id } = useParams()

  const redirect = !isSignedIn || !lineItems || lineItems.length === 0

  return redirect ? (
    <Redirect to={`/facilities/${facility_id}/register`} />
  ) : (
    <div className="min-h-screen bg-gray-50 relative">
      <Confetti width={width} height={height} recycle={false} initialVelocityY={-20} numberOfPieces={500} />

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Registration Complete!</h1>
          <p className="text-lg text-gray-600">
            {facility?.name ? `Your payment to ${facility.name} was successful` : 'Your payment was successful'}
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
            <CardDescription>Here&apos;s what you purchased</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lineItems.map((lineItem) => (
                <div
                  key={lineItem.id}
                  className="flex justify-between items-start py-3 border-b border-gray-200 last:border-0"
                >
                  <span className="text-sm font-medium text-gray-700">{lineItem.description}</span>
                  <span className="text-sm font-semibold text-gray-900 ml-4">
                    ${(lineItem.price / 100.0).toFixed(2)}
                  </span>
                </div>
              ))}

              {couponCodeAmount && (
                <div className="flex justify-between items-start py-3 border-b border-gray-200 text-green-700">
                  <span className="text-sm font-medium">Coupon Code for {couponCodePromotionAmount} off</span>
                  <span className="text-sm font-semibold ml-4" data-cy="coupon-code-amount">
                    -${priceInDollars(couponCodeAmount)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center pt-4">
                <span className="text-lg font-bold text-gray-900">Total</span>
                <span className="text-lg font-bold text-gray-900">${(total / 100.0).toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="space-y-3 text-center">
              <p className="text-gray-700">You will receive an email receipt shortly</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-gray-700">Or</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={window.print}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  print this page
                </Button>
                <span className="text-gray-700">for your records</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={() => history.push(`/guardians/${currentUser.id}/students`)}
            className="bg-purple-600 hover:bg-purple-700 px-8"
            data-cy="parent-dashboard-call-to-action"
          >
            Go to Your Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  )
}

PaymentConfirmation.propTypes = {
  lineItems: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  couponCodeAmount: PropTypes.number,
  couponCodePromotionAmount: PropTypes.string
}

export default PaymentConfirmation
